import React from "react";
import { Link, Navigate } from "react-router-dom";
import {
    ArrowLeft,
    Server,
    Crown,
    Package,
    ShoppingBag,
    Receipt,
    Sparkles,
    ExternalLink,
    LayoutGrid,
    ShieldCheck,
} from "lucide-react";
import ScrollReveal from "./ScrollReveal";
import { userDisplayName } from "@/lib/userDisplay";
import { extractDiscordIdentity } from "@/lib/discordIdentity";
import { usePlayerProfile } from "@/hooks/usePlayerProfile";
import SteamIdBlock from "@/components/auth/SteamIdBlock";
import { format, parseISO, isValid } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { useI18n } from "@/i18n/I18nProvider";
import { ADMIN_PATH, isAdminUser } from "@/lib/adminPortal";

const cardSurface = {
    background:
        "linear-gradient(180deg, rgba(26,15,10,0.94), rgba(10,7,6,0.9))",
};

function formatMemberSince(iso, lang) {
    if (!iso) return "—";
    try {
        const d = typeof iso === "string" ? parseISO(iso) : new Date(iso);
        if (!isValid(d)) return "—";
        return format(d, "d MMM yyyy", { locale: lang === "en" ? enUS : ptBR });
    } catch {
        return "—";
    }
}

function DashCard({ children, className = "" }) {
    return (
        <div
            className={`rounded-2xl border border-white/10 p-5 md:p-6 gold-border-hover ${className}`}
            style={cardSurface}
        >
            {children}
        </div>
    );
}

function QuickAction({ to, icon: Icon, title, subtitle }) {
    return (
        <Link
            to={to}
            className="group flex items-start gap-3 rounded-2xl border border-white/10 p-4 md:p-5 transition-[border-color,box-shadow] duration-300 hover:border-gold/35 hover:shadow-[0_0_0_1px_rgba(255,184,0,0.08),0_16px_40px_-24px_rgba(255,184,0,0.15)]"
            style={cardSurface}
        >
            <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10"
                style={{
                    background: "rgba(255,184,0,0.06)",
                    borderColor: "rgba(255,184,0,0.2)",
                    color: "#FFB800",
                }}
            >
                <Icon size={22} strokeWidth={1.65} aria-hidden />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex items-center gap-1.5">
                    <span className="text-[14px] font-semibold text-zrs-offwhite group-hover:text-gold transition-colors">
                        {title}
                    </span>
                </div>
                <p className="mt-1 text-[12px] leading-snug text-zrs-metal">{subtitle}</p>
            </div>
        </Link>
    );
}

function EmptyPanel({ icon: Icon, title, body }) {
    return (
        <div
            className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-white/12 px-5 py-12 md:py-14 text-center"
            style={{
                background: "linear-gradient(180deg, rgba(12,8,6,0.5), rgba(8,6,5,0.35))",
            }}
        >
            <div
                className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 text-zrs-metal/80"
                style={{ background: "rgba(0,0,0,0.25)" }}
            >
                <Icon size={28} strokeWidth={1.5} aria-hidden />
            </div>
            <h3 className="text-[15px] font-bold text-zrs-offwhite/90">{title}</h3>
            <p className="mt-2 max-w-xs text-[13px] leading-relaxed text-zrs-metal">{body}</p>
        </div>
    );
}

export default function UserDashboard({ user, discordUrl }) {
    const { profile, savingSteam, saveSteamId, hasSteamId } = usePlayerProfile(user);
    const { t, lang } = useI18n();

    if (!user) {
        return <Navigate to="/" replace />;
    }

    const name = userDisplayName(user);
    const avatar = user.user_metadata?.avatar_url;
    const memberSince = formatMemberSince(user.created_at, lang);
    const discord = extractDiscordIdentity(user, profile);

    return (
        <div
            data-testid="user-dashboard"
            className="relative min-h-[calc(100vh-5rem)] pt-28 pb-20 md:pt-32 md:pb-28 px-6 md:px-8"
            style={{
                background:
                    "radial-gradient(ellipse 70% 45% at 50% 0%, rgba(107,51,36,0.14) 0%, transparent 55%), #050505",
            }}
        >
            <div className="relative z-10 mx-auto max-w-6xl">
                <ScrollReveal>
                    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <Link
                            to="/"
                            className="inline-flex w-fit items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-zrs-offwhite/80 transition-colors hover:border-gold/35 hover:bg-white/[0.04] hover:text-gold"
                        >
                            <ArrowLeft size={16} aria-hidden />
                            {t("account.backToSite")}
                        </Link>
                        {isAdminUser(user) ? (
                            <Link
                                to={ADMIN_PATH}
                                data-testid="account-admin-portal-link"
                                className="btn-gold inline-flex w-fit items-center gap-2 rounded-full px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em]"
                            >
                                <ShieldCheck size={16} aria-hidden />
                                {t("account.adminPortal")}
                            </Link>
                        ) : null}
                    </div>
                </ScrollReveal>

                <ScrollReveal delay={60}>
                    <div className="mb-10 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                        <div className="flex min-w-0 flex-1 items-start gap-4">
                            {avatar ? (
                                <img
                                    src={avatar}
                                    alt=""
                                    className="h-16 w-16 shrink-0 rounded-2xl border border-white/10 object-cover md:h-20 md:w-20"
                                />
                            ) : (
                                <div
                                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-white/10 text-gold md:h-20 md:w-20"
                                    style={{ background: "rgba(255,184,0,0.08)" }}
                                    aria-hidden
                                >
                                    <LayoutGrid size={32} strokeWidth={1.5} />
                                </div>
                            )}
                            <div className="min-w-0">
                                <div className="text-[11px] uppercase tracking-[0.4em] text-gold/80">
                                    {t("account.eyebrow")}
                                </div>
                                <h1 className="mt-2 text-3xl font-bold leading-tight text-zrs-offwhite md:text-4xl">
                                    {t("account.hello")}{" "}
                                    <span className="text-gold gold-glow-text">{name}</span>
                                </h1>
                                <p className="mt-2 max-w-xl text-[14px] leading-relaxed text-zrs-offwhite/60">
                                    {t("account.intro")}
                                </p>
                            </div>
                        </div>
                    </div>
                </ScrollReveal>

                <ScrollReveal delay={80}>
                    <DashCard className="mb-10">
                        <div className="mb-4 flex items-center gap-3 border-b border-white/10 pb-4">
                            {discord.discordAvatarUrl ? (
                                <img
                                    src={discord.discordAvatarUrl}
                                    alt=""
                                    className="h-14 w-14 rounded-xl border border-white/10 object-cover"
                                />
                            ) : null}
                            <div>
                                <div className="text-[15px] font-bold text-zrs-offwhite">
                                    {discord.discordGlobalName}
                                </div>
                                <div className="text-[13px] text-zrs-metal">{discord.discordTag}</div>
                            </div>
                        </div>
                        <h3 className="text-[15px] font-bold text-zrs-offwhite mb-1">{t("account.player.title")}</h3>
                        <p className="text-[12px] text-zrs-offwhite/65 mb-4">
                            {hasSteamId
                                ? t("account.player.steamLinked")
                                : t("account.player.steamRequired")}
                        </p>
                        <SteamIdBlock
                            steamId={profile?.steam_id}
                            saving={savingSteam}
                            onSave={saveSteamId}
                        />
                    </DashCard>
                </ScrollReveal>

                <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <ScrollReveal delay={100}>
                        <DashCard>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-zrs-metal mb-2">
                                {t("account.card.accountLabel")}
                            </div>
                            <div className="text-[15px] font-bold text-zrs-offwhite">{t("account.card.discordLinked")}</div>
                            <p className="mt-2 text-[12px] leading-snug text-zrs-offwhite/65">
                                {t("account.card.discordBody")}
                            </p>
                        </DashCard>
                    </ScrollReveal>
                    <ScrollReveal delay={140}>
                        <DashCard>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-zrs-metal mb-2">
                                {t("account.card.statusLabel")}
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="relative flex h-2 w-2">
                                    <span className="absolute inset-0 rounded-full bg-[#44d264] status-pulse" />
                                    <span className="relative inline-flex h-2 w-2 rounded-full bg-[#44d264]" />
                                </span>
                                <span className="text-[15px] font-bold text-[#7FD89B]">{t("account.card.active")}</span>
                            </div>
                            <p className="mt-2 text-[12px] text-zrs-offwhite/60">
                                {t("account.card.activeBody")}
                            </p>
                        </DashCard>
                    </ScrollReveal>
                    <ScrollReveal delay={180}>
                        <DashCard>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-zrs-metal mb-2">
                                {t("account.card.memberSince")}
                            </div>
                            <div className="text-xl font-bold text-gold md:text-2xl">{memberSince}</div>
                            <p className="mt-2 text-[12px] text-zrs-offwhite/60">
                                {t("account.card.memberSinceBody")}
                            </p>
                        </DashCard>
                    </ScrollReveal>
                </div>

                <ScrollReveal delay={100}>
                    <div className="mb-4 flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-gold/80" aria-hidden />
                        <h2 className="text-[11px] font-bold uppercase tracking-[0.28em] text-gold/85">
                            {t("account.quick.title")}
                        </h2>
                    </div>
                </ScrollReveal>

                <div className="mb-12 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                    <ScrollReveal delay={120}>
                        <QuickAction
                            to="/#servidores"
                            icon={Server}
                            title={t("account.quick.servers")}
                            subtitle={t("account.quick.serversSub")}
                        />
                    </ScrollReveal>
                    <ScrollReveal delay={150}>
                        <QuickAction
                            to="/#vips"
                            icon={Crown}
                            title={t("account.quick.vip")}
                            subtitle={t("account.quick.vipSub")}
                        />
                    </ScrollReveal>
                    <ScrollReveal delay={180}>
                        <QuickAction
                            to="/#kits"
                            icon={Package}
                            title={t("account.quick.kits")}
                            subtitle={t("account.quick.kitsSub")}
                        />
                    </ScrollReveal>
                    <ScrollReveal delay={210}>
                        <QuickAction
                            to="/#loja"
                            icon={ShoppingBag}
                            title={t("account.quick.shop")}
                            subtitle={t("account.quick.shopSub")}
                        />
                    </ScrollReveal>
                    <ScrollReveal delay={240}>
                        <QuickAction
                            to="/vip/personalizado"
                            icon={Crown}
                            title={t("account.quick.simulator")}
                            subtitle={t("account.quick.simulatorSub")}
                        />
                    </ScrollReveal>
                </div>

                <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-lg font-bold text-zrs-offwhite md:text-xl">{t("account.summary.title")}</h2>
                    <a
                        href={discordUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[12px] font-semibold text-zrs-offwhite/90 transition-colors hover:border-gold/35 hover:bg-white/[0.04]"
                    >
                        {t("account.summary.discord")}
                        <ExternalLink size={14} className="text-gold/90" aria-hidden />
                    </a>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <ScrollReveal delay={80}>
                        <DashCard className="h-full flex flex-col">
                            <div className="mb-4 flex items-center gap-2 border-b border-white/10 pb-4">
                                <Receipt className="h-5 w-5 text-gold/85" aria-hidden />
                                <h3 className="text-[15px] font-bold text-zrs-offwhite">{t("account.history.title")}</h3>
                            </div>
                            <EmptyPanel
                                icon={Receipt}
                                title={t("account.history.emptyTitle")}
                                body={t("account.history.emptyBody")}
                            />
                        </DashCard>
                    </ScrollReveal>
                    <ScrollReveal delay={120}>
                        <DashCard className="h-full flex flex-col">
                            <div className="mb-4 flex items-center gap-2 border-b border-white/10 pb-4">
                                <ShoppingBag className="h-5 w-5 text-gold/85" aria-hidden />
                                <h3 className="text-[15px] font-bold text-zrs-offwhite">{t("account.cart.title")}</h3>
                            </div>
                            <EmptyPanel
                                icon={ShoppingBag}
                                title={t("account.cart.emptyTitle")}
                                body={t("account.cart.emptyBody")}
                            />
                        </DashCard>
                    </ScrollReveal>
                </div>
            </div>

            <div
                className="pointer-events-none absolute bottom-0 inset-x-0 h-32"
                style={{
                    background: "linear-gradient(to bottom, transparent, #050505 85%)",
                }}
            />
        </div>
    );
}
